import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    Output,
    QueryList,
    SimpleChanges,
    TemplateRef,
    ViewChildren
} from "@angular/core";
import { BalancePipe } from "@karma-games/angular-devkit/src/lib/modules/pipes";
import { EControlType, ICustomLoaderData } from "@karma-games/angular-devkit/src/lib/interfaces";
import { IStepperConfig, SelectComponent } from "@karma-games/angular-devkit/src/lib/modules/controls-components";
import { IAutoplayOptions, ISendBetArgs } from "../core/round/store/interfaces";
import { EBetButtonState, EBetPanelTab, IBetButtonState } from "./interfaces";
import { cloneDeep } from "lodash";
import { betButtonStates, betPanelStepperDefaultConfig } from "./constants";
import { IAutoplayOptionsConfig } from "../core/config/interfaces";
import { EDirectionY, EXCoordAlignment, EYCoordAlignment } from "@karma-games/angular-devkit/src/lib/modules/tooltip";
import { buttonLoaderImage } from "../shared/constants/button-loader-image-base64";
import { EButtonModifier } from "@karma-games/angular-devkit/src/lib/modules/button";
import { WalletAnimationDirective } from "@karma-games/angular-devkit/src/lib/modules/wallet-animation";
import { EBetSplit } from "../core/round/interfaces";
import IOption = SelectComponent.IOption;

@Component({
    selector: "bet-panel",
    templateUrl: "./bet-panel.component.html",
    styleUrls: ["./bet-panel.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BetPanelComponent {
    @ViewChildren(WalletAnimationDirective, { read: WalletAnimationDirective }) wonAmountElements: QueryList<WalletAnimationDirective> = new QueryList<WalletAnimationDirective>();
    @Input() disabled: boolean = false;
    @Input() balanceAvailable: boolean = false;

    @Input() set stateName(data: EBetButtonState) {
        this.betButtonState.stateName = data;
        this.betButtonSoundType = this.getBetButtonSoundType(data);
    };

    @Input() set buttonModifier(data: EButtonModifier) {
        this.betButtonState.buttonModifier = data;
    };

    @Input() set textKey(data: string) {
        this.betButtonState.textKey = data;
    };

    @Input() set showAmountInText(data: boolean) {
        this.betButtonState.showAmountInText = data;
    };

    @Input() set actionInProgress(data: boolean) {
        this.betButtonState.actionInProgress = data;
    };

    @Input() set accumulatedAmount(data: number | undefined) {
        this.betButtonState.accumulatedAmount = data;
    };

    @Input() set roundId(data: string | undefined) {
        this.betButtonState.roundId = data;
    };

    @Input() set multiplier(data: number | undefined) {
        this.betButtonState.multiplier = data;
    };

    @Input() set autoplayRoundCount(data: number | undefined) {
        this.betButtonState.autoplayRoundCount = data;
    };

    @Input() predefinedBetOptions: IOption<number>[] = [];

    @Input() set betStepperConfig(data: IStepperConfig | undefined) {
        if (!data) {
            return;
        }
        this.betPanelStepperConfig = cloneDeep(data);
    }

    @Input() set autoplayConfig(data: IAutoplayOptionsConfig | undefined) {
        this.betPanelAutoplayConfig = cloneDeep(data);
    }

    @Input() set multiplierConfig(data: IStepperConfig | undefined) {
        if (!data) {
            return;
        }
        this.multiplierStepperConfig = cloneDeep(data);
        this.selectedAutoCashoutMultiplier = this.multiplierStepperConfig.initialValue;
    }

    @Input() betIndex: number = 0;
    @Input() firstHalfCashedOut: number | undefined = undefined;
    @Input() multiPanelButtonDisabled: boolean = false;
    @Output() multiPanelButtonClicked: EventEmitter<void> = new EventEmitter();
    @Output() onAutoplayStart: EventEmitter<IAutoplayOptions> = new EventEmitter<IAutoplayOptions>();
    @Output() onSendBet: EventEmitter<ISendBetArgs> = new EventEmitter<ISendBetArgs>();
    @Output() onCancelBet: EventEmitter<number> = new EventEmitter<number>();
    @Output() onCashOut: EventEmitter<{betIndex: number, split?: EBetSplit}> = new EventEmitter<{betIndex: number, split?: EBetSplit}>();
    @Output() removeAutoplay: EventEmitter<void> = new EventEmitter<void>();
    @Output() onAutoPlayOptionsClicked: EventEmitter<TemplateRef<any>> = new EventEmitter<TemplateRef<any>>();

    public betButtonState: IBetButtonState = cloneDeep(betButtonStates[EBetButtonState.NoBet]);
    public buttonLoaderImage: ICustomLoaderData = {
        url: buttonLoaderImage,
        forceRotate: true,
        additionalStyles: {
            height: "4em",
            width: "4em"
        }
    };
    public EDirectionY: typeof EDirectionY = EDirectionY;
    public EButtonModifier: typeof EButtonModifier = EButtonModifier;
    public EXCoordAlignment: typeof EXCoordAlignment = EXCoordAlignment;
    public EYCoordAlignment: typeof EYCoordAlignment = EYCoordAlignment;
    public hintStyles: { [key: string]: string } = {
        marginBottom: "0.4em",
        maxWidth: "85%"
    };
    public betPanelStepperConfig: IStepperConfig = betPanelStepperDefaultConfig;
    public multiplierStepperConfig: IStepperConfig = betPanelStepperDefaultConfig;
    public betPanelAutoplayConfig: IAutoplayOptionsConfig | undefined = undefined;
    public betOptions: IOption<number>[] = [];
    public tabs: IOption<EBetPanelTab>[] = [
        { text: { key: "MANUAL_BET" }, value: EBetPanelTab.Bet },
        { text: { key: "AUTO" }, value: EBetPanelTab.AutoBet }
    ];
    public currentTab: IOption<EBetPanelTab> = this.tabs[0];
    public controlType: typeof EControlType = EControlType;
    public selectedBetAmount: number = this.betPanelStepperConfig.initialValue;
    public autoCashOutMultiplierEnabled: boolean = false;
    public selectedAutoCashoutMultiplier: number = this.multiplierStepperConfig.initialValue;
    public EBetButtonState: typeof EBetButtonState = EBetButtonState;
    public EBetSplit: typeof EBetSplit = EBetSplit;
    public betButtonSoundType: string | null = this.getBetButtonSoundType(EBetButtonState.NoBet);
    public autoCashOutMaxValue: number = this.multiplierStepperConfig.maxValue;

    constructor(private balancePipeService: BalancePipe,
                private cdr: ChangeDetectorRef) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["predefinedBetOptions"]?.currentValue) {
            this.betOptions = changes["predefinedBetOptions"].currentValue;
        }
    }

    onTabChange(tab: IOption<EBetPanelTab>): void {
        this.currentTab = tab;
        if (this.currentTab.value === EBetPanelTab.Bet) {
            this.autoCashOutMultiplierEnabled = false;
        }
    }

    startAutoplay(options: Partial<IAutoplayOptions> & { autoCashOut?: number }): void {
        if (options.autoCashOut) {
            this.updateMultiplierSelectedValue(options.autoCashOut);
            this.onMultiplierCashOutChange(true);
            delete options.autoCashOut;
        }
        this.onAutoplayStart.emit(<IAutoplayOptions>{
            betArgs: {
                amount: this.selectedBetAmount, betIndex: this.betIndex,
                maxMultiplier: this.autoCashOutMultiplierEnabled ? this.selectedAutoCashoutMultiplier : undefined
            },
            ...options
        });
    }

    formatBalanceStepperNumber = (value: number) => {
        return this.balancePipeService.transform(value, true, true) ?? value.toString();
    };

    formatStepperNumber = (value: number) => {
        return `x${value.toFixed(2)}`;
    };

    updateSelectedValue(amount: number): void {
        this.selectedBetAmount = amount;
        this.betPanelStepperConfig.initialValue = this.selectedBetAmount;
        this.autoCashOutMaxValue = Math.min(this.multiplierStepperConfig.maxValue / this.selectedBetAmount, this.multiplierStepperConfig.maxValue);
        this.selectedAutoCashoutMultiplier = Math.min(this.selectedAutoCashoutMultiplier, this.autoCashOutMaxValue);
    }

    updateMultiplierSelectedValue(amount: number): void {
        this.selectedAutoCashoutMultiplier = amount;
        this.multiplierStepperConfig.initialValue = this.selectedBetAmount;
    }

    sendBet = (): void => {
        this.onSendBet.emit({
            amount: this.selectedBetAmount, betIndex: this.betIndex,
            maxMultiplier: this.autoCashOutMultiplierEnabled ? this.selectedAutoCashoutMultiplier : undefined
        });
    };

    cancelBet = (): void => {
        this.onCancelBet.emit(this.betIndex);
    };

    cashOut = (split?: EBetSplit): void => {
        console.warn("CASH OUT BUTTON CLICKED TIME: ", Date.now());
        this.onCashOut.emit({betIndex: this.betIndex, split});
    };

    handleAction(betButtonStateName: EBetButtonState, split?: EBetSplit): void {
        switch (betButtonStateName) {
            case EBetButtonState.NoBet:
            case EBetButtonState.BetNotPrepared:
                this.sendBet();
                break;
            case EBetButtonState.Betted:
            case EBetButtonState.BetPrepared:
                this.cancelBet();
                break;
            case EBetButtonState.RoundPlaying:
                this.cashOut(split);
                break;
        }
    }

    onMultiplierCashOutChange(enabled: boolean): void {
        this.autoCashOutMultiplierEnabled = enabled;
        this.cdr.detectChanges();
    }

    getBetButtonSoundType(state: EBetButtonState): string | null {
        switch (state) {
            case EBetButtonState.NoBet:
                return "BetPressed";
            case EBetButtonState.Betted:
            case EBetButtonState.RoundPlaying:
                return "Click";
            default:
                return "Click";
        }
    }

    startWalletAnimation(split: EBetSplit): void {
        const wonElement: WalletAnimationDirective | undefined = this.wonAmountElements.toArray()[split === EBetSplit.FirstHalf ? 0 : 1];
        if (wonElement) {
            wonElement.startAnimation();
        }
    }

    getAutoPlayOptionsConfig(): IAutoplayOptionsConfig & { autoCashOut: IStepperConfig & { enabled: boolean } } {
        return {
            ...<IAutoplayOptionsConfig>this.betPanelAutoplayConfig,
            autoCashOut: {
                ...this.multiplierStepperConfig,
                initialValue: this.selectedAutoCashoutMultiplier,
                enabled: this.autoCashOutMultiplierEnabled
            }
        };
    }
}
